import { useVirtualizer } from '@tanstack/react-virtual';
import { startOfDay } from 'date-fns';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { VirtualChatRow } from '../../../types';

const ESTIMATED_ROW_HEIGHT = 58;
const SCROLL_END_THRESHOLD = 80;
/*
 * Keep every message row mounted (no overscan limit). With a normal overscan, rows unmount off-screen;
 * ReadMore then remounts collapsed and loses expand state. Scrolling away and back looks like the bubble
 * closed on its own. Full mount lets ReadMore measure height and keep expanded/collapsed state stable.
 */
const VIRTUALIZER_OVERSCAN = Number.POSITIVE_INFINITY;

type P = {
  rows: VirtualChatRow[];
};

const getRowKey = (row: undefined | VirtualChatRow, index: number) => {
  if (!row) {
    return index;
  }

  if (row.kind === 'day-divider') {
    return `day-divider-${startOfDay(row.date).toISOString()}`;
  }

  return row.message.id;
};

export const useChatMessagesVirtualizer = ({ rows }: P) => {
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const [isAtEnd, setIsAtEnd] = useState(true);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns a stable instance with non-memoizable methods
  const virtualizer = useVirtualizer({
    anchorTo: 'end',
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    followOnAppend: true,
    getItemKey: (index) => getRowKey(rows[index], index),
    getScrollElement: () => scrollElementRef.current,
    onChange(instance) {
      setIsAtEnd(instance.isAtEnd());
    },
    overscan: VIRTUALIZER_OVERSCAN,
    scrollEndThreshold: SCROLL_END_THRESHOLD,
  });

  useLayoutEffect(() => {
    virtualizer.scrollToEnd();
  }, [virtualizer]);

  return useMemo(() => ({ isAtEnd, scrollElementRef, virtualizer }), [isAtEnd, virtualizer]);
};
